import React, { useEffect, useRef, useMemo, useState, useContext } from 'react';
import { flushSync } from 'react-dom';

import { Stack, xcss, Box } from '@atlaskit/primitives';
import { abortAll } from '@atlaskit/react-ufo/interaction-metrics';

import { componentWithFG } from '@confluence/feature-gate-component';
import { fg } from '@confluence/feature-gating';
import { getCommentPositionTopOfSection } from '@confluence/comments-util';
import { ReactionsProvider } from '@confluence/comment-context';
import { ObjectCommentQueryParamsListener } from '@confluence/comments-query-param-handlers';
import { withErrorBoundary, Attribution } from '@confluence/error-boundary';
import { withFlags } from '@confluence/flags';
import type { WithFlagsProps } from '@confluence/flags';
import { usePageContentId } from '@confluence/page-context';
import type { useActiveCommentsQueryVariables } from '@confluence/comments-data';
import {
	useCommentsDataState,
	useActiveCommentsQuery,
	CommentType,
	findCommentById,
	getTopLevelParentComment,
} from '@confluence/comments-data';
import {
	getAnnotationThreadKeys,
	getOpenCommentThreads,
	getVisibleCommentThreads,
	LocationValue,
	useCommentsPanelState,
	useCommentsPanelActions,
} from '@confluence/comments-panel-utils';
import { CommentPlaceholder } from '@confluence/comment-simple/entry-points/CommentPlaceholder';
import { useContentState } from '@confluence/content-state/entry-points/useContentState';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities';
import { ContentAppearanceType } from '@confluence/content-appearance';
import { CommentsType } from '@confluence/comments-panel-queries';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState';
import { useAnnotationsIsOffline } from '@confluence/annotation-provider-store';
import { useUnreadComments } from '@confluence/unread-comments';
import type { InlineCommentAuthorUser } from '@confluence/inline-comments-queries';
import {
	ExperienceTrackerContext,
	VIEW_PAGE_COMMENTS_EXPERIENCE,
} from '@confluence/experience-tracker';
import { PAGE_COMMENTS_METRIC } from '@confluence/comments-section/entry-points/pageSegmentLoadMetrics';

import { ObjectAddCommentContainer } from './ObjectAddCommentContainer';
import { ObjectCommentsList } from './ObjectCommentsList';
import { ObjectCommentHeader } from './ObjectCommentHeader';
import { ObjectCommentLegacyContainerComponent } from './ObjectCommentLegacyContainer';

const commentPanelContainerStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	height: '100%',
	width: '100%',
});

const wrapperStyle = xcss({
	position: 'relative',
	visibility: 'visible',
	minHeight: '50px',
	margin: '0 auto',
	width: '100%',
});

const placeholderStyle = xcss({
	width: '60%',
});

const addCommentContainerStyles = xcss({
	transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
	transformOrigin: 'top',
	position: 'sticky',
	bottom: 'space.0',
	zIndex: '1', // Required for create comment editor overflow to show on top of page content
});

const addCommentContainerAnimationHiddenStyles = xcss({
	transform: 'translateY(100%)',
	opacity: 0,
	pointerEvents: 'none',
});

const addCommentContainerAnimationShowStyles = xcss({
	transform: 'translateY(0)',
	opacity: 1,
	pointerEvents: 'auto',
});

type ObjectCommentContainerProps = {
	pageWidthType?: string;
};

const useFocusedCommentIdQueryParam = createSingleQueryParamHook('focusedCommentId');
const useReplyToCommentQueryParam = createSingleQueryParamHook('replyToComment');

const ObjectCommentContainerComponent = withFlags(
	({ flags, pageWidthType }: WithFlagsProps & ObjectCommentContainerProps) => {
		const [contentId] = usePageContentId();
		const wrapperRef = useRef<HTMLDivElement>(null);
		const [commentSectionTop, setCommentSectionTop] = useState<number | undefined>(undefined);
		const [isInViewport, setIsInViewport] = useState(false);
		const [allCommentsRendered, setAllCommentsRendered] = useState(false);
		const [commentBatchRendered, setCommentBatchRendered] = useState(false);
		const [hasLoadedComments, setHasLoadedComments] = useState(false);
		const [forceLoadComments, setForceLoadComments] = useState(false);
		const [state] = useContentState();
		const pageMode = useGetPageMode();
		const isServer = useRenderServerPlaceholder();
		const { orderedActiveAnnotationIdList, commentsDataMap, removedThreadsMap, hasNestedComments } =
			useCommentsDataState();
		const focusedCommentId = useFocusedCommentIdQueryParam();
		const replyToComment = useReplyToCommentQueryParam();
		const hasScrolledToFocusedComment = useRef(false);
		const hasScrolledToReplyComment = useRef(false);
		const wasSSRed = useRef<boolean | undefined>(undefined);

		const {
			showInlineComments,
			showGeneralComments,
			replyToCommentId,
			commentType,
			currentlySelectedThreadKey,
			location: commentLocation,
		} = useCommentsPanelState();

		const { setCurrentlySelectedThreadKey, setReplyToCommentId } = useCommentsPanelActions();
		const [{ isUnreadQueryLoading }] = useUnreadComments();
		const [hasLoadedUnreadStatus, setHasLoadedUnreadStatus] = useState(false);
		const experienceTracker = useContext(ExperienceTrackerContext);

		const isContentReady = pageMode === PageMode.VIEW ? state.isContentReady : true;

		// AKA - has the user scrolled to the bottom of the page?
		const isContentInViewport = isInViewport && isContentReady;

		// Query setup - skip if not forcing load and not in viewport and not loaded yet
		const commentsQueryHook = useActiveCommentsQuery;

		// Load conditions
		// Either we need to load based on url params / have scrolled down to the section
		// Or we need to prevent loading because we've already loaded (either though SPA or SSR)
		// Or we need to prevent loading because we're waiting on prerequisite data (unread status)
		const commentsQueryHookVariables: useActiveCommentsQueryVariables = {
			pageId: contentId || '',
			skip:
				(!forceLoadComments && !isContentInViewport) ||
				isServer ||
				wasSSRed.current ||
				hasLoadedComments ||
				!hasLoadedUnreadStatus,
			type: CommentsType.FOOTER,
			returnNativeLoading: true, // required for `loading` to behave as expected
		};

		const { loading, error, data } = commentsQueryHook(commentsQueryHookVariables);

		const noOpenObjectComments = useMemo(() => {
			return data?.comments?.nodes?.length === 0 || true;
		}, [data]);

		// This is the same query as above, but re-using the variables in both cases causes a lot of cognitive complexity in deciding when to consider loading complete
		// Until we stop only SSRing empty sections, keeping this separate saves a lot of headaches
		const preloadCommentsQueryHook = useActiveCommentsQuery;
		const preloadCommentsQueryHookVariables: useActiveCommentsQueryVariables = {
			pageId: contentId || '',
			type: CommentsType.FOOTER,
			skip: !isServer, // only run this on the server to get cached data from the preload query
			isPreload: true,
		};

		const { data: preloadData } = preloadCommentsQueryHook(preloadCommentsQueryHookVariables);

		// SSR if there are no comments and we're on the server
		const totalCount = preloadData?.comments?.totalCount;
		const shouldSSR = totalCount === 0 && isServer;
		wasSSRed.current = wasSSRed.current || shouldSSR;

		// AKA - are we done loading?
		const shouldShowComments = (hasLoadedComments && hasLoadedUnreadStatus) || wasSSRed.current;

		// Return null when we haven't loaded anything yet, we're not in the viewport, and we're not SSRed
		const shouldReturnNull = error || (!isContentInViewport && !shouldShowComments);

		// Show placeholder when we're in the viewport but are still loading, and we're not SSRed
		const shouldShowPlaceholder = isContentInViewport && !shouldShowComments;

		// Set up IntersectionObserver
		useEffect(() => {
			if (isServer || !isContentReady) {
				// Don't register observer if page content is not ready
				return;
			}

			// eslint-disable-next-line no-restricted-syntax
			const observer = new IntersectionObserver(
				([entry]) => {
					const isIntersecting = entry.isIntersecting;
					if (isIntersecting) {
						setIsInViewport(isIntersecting);
					}
				},
				{
					root: null,
					rootMargin: '10px',
					threshold: 0.1,
				},
			);

			if (wrapperRef.current) {
				observer.observe(wrapperRef.current);
			}

			return () => {
				observer.disconnect();
			};
		}, [isServer, pageMode, state.isContentReady, isContentReady]);

		// Add MutationObserver to detect when comments are actually rendered
		useEffect(() => {
			if (isServer) {
				return;
			}

			const observer = new MutationObserver(() => {
				const commentElementId = `footer-comment-thread-${currentlySelectedThreadKey}-container`;
				const commentElement = document.getElementById(commentElementId);

				if (commentElement) {
					setAllCommentsRendered(true);
					observer.disconnect(); // Disconnect once we find the element
				} else {
					setAllCommentsRendered(false); // Reset if element not found
				}
			});

			observer.observe(document.body, {
				childList: true,
				subtree: true,
			});

			// Reset allCommentsRendered when unmounting or changing thread key
			return () => {
				setAllCommentsRendered(false);
				observer.disconnect();
			};
		}, [currentlySelectedThreadKey, isServer]);

		// Track the start of comments loading and initialize performance metrics
		useEffect(() => {
			if (loading && fg('confluence_frontend_view_page_comments_exp_new')) {
				// Start tracking the page comments experience with initial attributes
				experienceTracker.start({
					name: VIEW_PAGE_COMMENTS_EXPERIENCE,
					attributes: {
						isConditionalRenderingOn: true,
						isFocusedComment: Boolean(focusedCommentId || replyToComment),
						contentId,
						pageMode,
					},
				});

				// Initialize and mark the start of page comments rendering metrics
				PAGE_COMMENTS_METRIC.start();
				PAGE_COMMENTS_METRIC.mark('pageCommentsRenderStart');
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [loading]);

		// Track any errors that occur during comments loading
		useEffect(() => {
			if (error && fg('confluence_frontend_view_page_comments_exp_new')) {
				// Mark the experience as failed and include the error details
				experienceTracker.fail({
					name: VIEW_PAGE_COMMENTS_EXPERIENCE,
					error,
				});
				PAGE_COMMENTS_METRIC.cancel();
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [error]);

		// Track successful completion of comments rendering
		useEffect(() => {
			if (allCommentsRendered && fg('confluence_frontend_view_page_comments_exp_new')) {
				// Mark the experience as successful and include total comments count
				experienceTracker.succeed({
					name: VIEW_PAGE_COMMENTS_EXPERIENCE,
					attributes: {
						totalComments: data?.comments?.totalCount,
					},
				});

				// Mark key performance metrics:
				// - pageCommentsRenderEnd: End of comments rendering
				PAGE_COMMENTS_METRIC.mark('pageCommentsRenderEnd');
				// Stop the metric tracking and include final data
				PAGE_COMMENTS_METRIC.stop({
					customData: {
						contentId: contentId || '',
						totalComments: data?.comments?.totalCount || 0,
					},
				});
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [allCommentsRendered]);

		// Track successful completion of first comment batch rendering
		useEffect(() => {
			if (commentBatchRendered && fg('confluence_frontend_view_page_comments_exp_new')) {
				// Mark key performance metrics:
				// - fmp: First Meaningful Paint
				// - tti: Time to Interactive
				PAGE_COMMENTS_METRIC.markFMP();
				PAGE_COMMENTS_METRIC.mark('tti');

				const experienceState = experienceTracker.getExperienceState(VIEW_PAGE_COMMENTS_EXPERIENCE);

				if (experienceState?.startTime && typeof experienceState.startTime === 'number') {
					experienceTracker.updateAttributes({
						name: VIEW_PAGE_COMMENTS_EXPERIENCE,
						attributes: {
							firstMeaningfulPaint: window.performance.now() - experienceState.startTime,
						},
					});
				}
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [commentBatchRendered]);

		// Handle case when there are no comments to render
		useEffect(() => {
			if (
				data?.comments?.totalCount === 0 &&
				fg('confluence_frontend_view_page_comments_exp_new')
			) {
				// Abort the experience tracking since there are no comments to render
				experienceTracker.abort({
					name: VIEW_PAGE_COMMENTS_EXPERIENCE,
					reason: 'No page comments',
				});
				PAGE_COMMENTS_METRIC.cancel();
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [data]);

		// Update loaded state when data is received
		useEffect(() => {
			if ((data && !hasLoadedComments && !loading) || wasSSRed.current) {
				setHasLoadedComments(true);
				setForceLoadComments(false);
			}
		}, [data, hasLoadedComments, loading]);

		useEffect(() => {
			if (!isUnreadQueryLoading && !hasLoadedUnreadStatus) {
				setHasLoadedUnreadStatus(true);
			}
		}, [isUnreadQueryLoading, hasLoadedUnreadStatus]);

		// Handle scrolling after comments are loaded
		useEffect(() => {
			if (shouldShowComments && commentType === CommentType.GENERAL && allCommentsRendered) {
				const commentElementId = `footer-comment-thread-${currentlySelectedThreadKey}-container`;
				const isCommentExists = document.getElementById(commentElementId);
				if (isCommentExists) {
					const rect = isCommentExists.getBoundingClientRect();
					const commentIsInViewport =
						rect.top >= 0 &&
						rect.left >= 0 &&
						rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
						rect.right <= (window.innerWidth || document.documentElement.clientWidth);
					if (!commentIsInViewport) {
						isCommentExists.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
							inline: 'center',
						});
					}
				}
			}
		}, [
			hasLoadedComments,
			commentType,
			currentlySelectedThreadKey,
			allCommentsRendered,
			hasLoadedUnreadStatus,
			shouldShowComments,
		]);

		useEffect(() => {
			if (commentType === CommentType.GENERAL) {
				const commentElementId = `footer-comment-thread-${currentlySelectedThreadKey}-container`;
				const isCommentExists = document.getElementById(commentElementId);
				if (!isCommentExists) {
					setForceLoadComments(true);
				}
			}
		}, [currentlySelectedThreadKey, commentType]);

		// Handle editor resize
		useEffect(() => {
			const editorTextArea = document.getElementById('ak-editor-textarea');
			if (editorTextArea) {
				const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
					if (fg('confluence_footer_comment_styling_change')) {
						const newTop = getCommentPositionTopOfSection(entries);
						if (fg('confluence_footer_comment_styling_fix_flicker')) {
							flushSync(() => {
								newTop && setCommentSectionTop(newTop);
							});
						} else {
							newTop && setCommentSectionTop(newTop);
						}
					}
				});
				resizeObserver.observe(editorTextArea);
				return () => resizeObserver.disconnect();
			}
		}, []);

		// Handle scrolling to focused comment when data is loaded
		useEffect(() => {
			if (
				shouldShowComments &&
				(focusedCommentId || replyToComment) &&
				allCommentsRendered &&
				!hasScrolledToFocusedComment.current &&
				!hasScrolledToReplyComment.current &&
				!fg('confluence_frontend_resolved_page_comment_links')
			) {
				const commentElementId = `footer-comment-thread-${focusedCommentId || replyToComment}-container`;
				const replyCommentElementId = `footer-comment-thread-${focusedCommentId || replyToComment}-replies`;

				const commentElement = document.getElementById(commentElementId);
				const replyCommentElement = document.getElementById(replyCommentElementId);
				const element = commentElement || replyCommentElement;

				if (element) {
					if (replyToComment) {
						// Grab the comment object so we can get their user info
						const replyToCommentObject = findCommentById(commentsDataMap, replyToComment);
						const topLevelParentComment = getTopLevelParentComment(commentsDataMap, replyToComment);

						// If nested comments aren't allowed, make sure we're replying to the top level parent
						const commentToReplyTo = hasNestedComments
							? replyToCommentObject
							: topLevelParentComment;

						// No matter what, mention the intended user
						const replyToUser = {
							// This field controls who we auto mention in the reply
							displayName: replyToCommentObject?.author?.displayName,
							accountId: (replyToCommentObject?.author as InlineCommentAuthorUser)?.accountId,
						};
						// Thread key should be handled by the top level parent
						setCurrentlySelectedThreadKey(topLevelParentComment?.id ?? replyToComment);

						setReplyToCommentId({
							parentCommentId: commentToReplyTo?.id,
							location: LocationValue.FOOTER,
							user: replyToUser,
						});
						hasScrolledToReplyComment.current = true;
						return;
					}
					const rect = element.getBoundingClientRect();
					const commentIsInViewport =
						rect.top >= 0 &&
						rect.left >= 0 &&
						rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
						rect.right <= (window.innerWidth || document.documentElement.clientWidth);
					if (!commentIsInViewport) {
						const topLevelParentCommentForFocusedComment = focusedCommentId
							? getTopLevelParentComment(commentsDataMap, focusedCommentId)
							: undefined;
						element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
						hasScrolledToFocusedComment.current = true;
						setCurrentlySelectedThreadKey(
							topLevelParentCommentForFocusedComment?.id ?? focusedCommentId,
						);
						// If the document scrolls to a focused comment on View or Live Page, the VC event is aborted.
						if (fg('confluence_frontend_abort_vc_on_auto_scroll')) {
							abortAll('new_interaction', `document-scroll:object-comment-container`);
						}
					}
				}
			}
		}, [
			hasLoadedComments,
			focusedCommentId,
			replyToComment,
			allCommentsRendered,
			setCurrentlySelectedThreadKey,
			setReplyToCommentId,
			hasLoadedUnreadStatus,
			shouldShowComments,
			hasNestedComments,
			commentsDataMap,
		]);

		// Reset scroll tracking when focusedCommentId or replyToComment changes
		useEffect(() => {
			if (!fg('confluence_frontend_resolved_page_comment_links')) {
				hasScrolledToFocusedComment.current = false;
				hasScrolledToReplyComment.current = false;
			}
		}, [focusedCommentId, replyToComment]);

		// Force load comments if focusedCommentId is present
		useEffect(() => {
			if (
				(focusedCommentId || replyToComment) &&
				!hasLoadedComments &&
				!fg('confluence_frontend_resolved_page_comment_links')
			) {
				setForceLoadComments(true);
			}
		}, [focusedCommentId, replyToComment, hasLoadedComments]);

		const commentThreads = useMemo(() => {
			if (!hasLoadedComments) {
				return [];
			}

			return fg('confluence_frontend_immediate_comment_removal')
				? getVisibleCommentThreads({
						commentsDataMap,
						includeGeneralComments: true,
						includeInlineComments: false,
						orderedAnnotationIds: getAnnotationThreadKeys(orderedActiveAnnotationIdList),
					})
				: getOpenCommentThreads({
						commentsDataMap,
						showInlineComments,
						showGeneralComments,
						location: LocationValue.FOOTER,
						orderedActiveAnnotationIdList,
						removedThreadsMap,
					});
		}, [
			hasLoadedComments,
			commentsDataMap,
			orderedActiveAnnotationIdList,
			removedThreadsMap,
			showInlineComments,
			showGeneralComments,
		]);

		const isOffline = useAnnotationsIsOffline();

		const getMaxWidth = () => {
			if (pageWidthType === undefined) {
				return '100%';
			}
			// pageWidthType is a string like '"default"'
			return JSON.parse(pageWidthType) === ContentAppearanceType.DEFAULT ? '1000px' : 'unset';
		};

		const hasComments = commentThreads.length > 0;

		return (
			<Box
				ref={wrapperRef}
				xcss={wrapperStyle}
				testId="object-comment-wrapper"
				style={{
					top: commentSectionTop,
					maxWidth: getMaxWidth(),
					// display: 'contents', // So wrapper doesn't affect layout if empty
				}}
				data-vc={`object-comment-wrapper-${pageMode}`}
			>
				<>
					{fg('confluence_frontend_resolved_page_comment_links') && (
						<ObjectCommentQueryParamsListener
							hasLoadedObjectComments={!!shouldShowComments}
							allObjectCommentsRendered={
								allCommentsRendered || noOpenObjectComments || wasSSRed.current
							}
							setForceLoadComments={setForceLoadComments}
						/>
					)}
					{shouldReturnNull ? null : (
						<>
							{shouldShowPlaceholder && (
								<Box xcss={placeholderStyle}>
									<CommentPlaceholder showPlaceholder />
								</Box>
							)}
							{shouldShowComments && (
								<ReactionsProvider contentId={contentId ?? ''}>
									{hasComments && <ObjectCommentHeader isOffline={isOffline} />}
									<Stack xcss={commentPanelContainerStyles}>
										<ObjectCommentsList
											commentThreads={commentThreads}
											supportedTopLevelActions={['edit', 'resolve', 'delete']}
											flags={flags}
											onAllCommentsRendered={() => setAllCommentsRendered(true)}
											onCommentBatchRendered={() => setCommentBatchRendered(true)}
											isOffline={isOffline}
										/>
									</Stack>
									<Box
										xcss={[
											addCommentContainerStyles,
											!!replyToCommentId && commentLocation === LocationValue.FOOTER
												? addCommentContainerAnimationHiddenStyles
												: addCommentContainerAnimationShowStyles,
										]}
									>
										{!shouldShowPlaceholder && (
											<ObjectAddCommentContainer
												showReactions={!hasComments}
												isOffline={isOffline}
												optimisticReactions={wasSSRed.current}
											/>
										)}
									</Box>
								</ReactionsProvider>
							)}
						</>
					)}
				</>
			</Box>
		);
	},
);

// This FG lets us quick revert without causing a HOT
// Don't update the legacy component, it will be removed shortly
export const ObjectCommentContainer = withErrorBoundary({
	attribution: Attribution.COMMENTS,
})(
	componentWithFG(
		'confluence_frontend_revert_ftr_cmts_load_chnges',
		ObjectCommentLegacyContainerComponent,
		ObjectCommentContainerComponent,
	),
);
